import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { MapPin, DollarSign, Briefcase, Calendar, Building2, Send, Bookmark, BookmarkCheck } from "lucide-react";

interface Opportunity {
  id: string;
  title: string;
  company_name: string;
  location: string;
  employment_type: string;
  description: string;
  salary_min: number;
  salary_max: number;
  disclosure_level_required: number;
  requirements: string | null;
  created_at: string;
}

interface JobDetailsModalProps {
  opportunity: Opportunity | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSaved?: boolean;
  hasApplied?: boolean;
  onSaveToggle?: () => void;
  onApply?: () => void;
}

export default function JobDetailsModal({
  opportunity,
  open,
  onOpenChange,
  isSaved = false,
  hasApplied = false,
  onSaveToggle,
  onApply,
}: JobDetailsModalProps) {
  const { toast } = useToast();
  const [coverLetter, setCoverLetter] = useState("");
  const [applying, setApplying] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!opportunity) return null;

  const formatSalary = (min: number, max: number) => {
    if (min && max) {
      return `£${min.toLocaleString()} - £${max.toLocaleString()}`;
    }
    return "Competitive";
  };

  const getDisclosureBadge = (level: number) => {
    const labels = ["No Check", "Basic DBS", "Standard DBS", "Enhanced DBS"];
    const variants: ("secondary" | "outline" | "default")[] = ["secondary", "outline", "outline", "default"];
    return { label: labels[level] || "Unknown", variant: variants[level] || "secondary" };
  };

  const handleApply = async () => {
    setApplying(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Check if already applied
      const { data: existing } = await supabase
        .from("job_applications")
        .select("id")
        .eq("candidate_id", user.id)
        .eq("job_posting_id", opportunity.id)
        .maybeSingle();

      if (existing) {
        toast({
          title: "Already Applied",
          description: "You have already applied to this position.",
          variant: "destructive",
        });
        return;
      }

      // Create application
      const { error } = await supabase.from("job_applications").insert({
        candidate_id: user.id,
        job_posting_id: opportunity.id,
        cover_letter: coverLetter || null,
        status: "pending",
      });

      if (error) throw error;

      toast({
        title: "Application Submitted",
        description: "Your application has been successfully submitted!",
      });

      setCoverLetter("");
      if (onApply) onApply();
    } catch (error: any) {
      console.error("Apply error:", error);
      toast({
        title: "Error Applying",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setApplying(false);
    }
  };

  const handleSaveToggle = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      if (isSaved) {
        // Remove from saved
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("candidate_id", user.id)
          .eq("opportunity_id", opportunity.id);

        if (error) throw error;

        toast({
          title: "Removed from Saved",
          description: "Job removed from your saved list.",
        });
      } else {
        // Add to saved
        const { error } = await supabase.from("saved_jobs").insert({
          candidate_id: user.id,
          opportunity_id: opportunity.id,
        });

        if (error) throw error;

        toast({
          title: "Job Saved",
          description: "Job added to your saved list.",
        });
      }

      if (onSaveToggle) onSaveToggle();
    } catch (error: any) {
      console.error("Save error:", error);
      toast({
        title: "Error Saving Job",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const disclosure = getDisclosureBadge(opportunity.disclosure_level_required);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl">{opportunity.title}</DialogTitle>
              <DialogDescription className="text-lg font-medium text-foreground mt-1">
                {opportunity.company_name}
              </DialogDescription>
            </div>
            <Button
              variant={isSaved ? "default" : "outline"}
              size="sm"
              onClick={handleSaveToggle}
              disabled={saving}
            >
              {isSaved ? (
                <>
                  <BookmarkCheck className="h-4 w-4 mr-2" />
                  Saved
                </>
              ) : (
                <>
                  <Bookmark className="h-4 w-4 mr-2" />
                  Save Job
                </>
              )}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job Details */}
          <div className="grid gap-3 md:grid-cols-2">
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>{opportunity.location}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Briefcase className="h-4 w-4 text-muted-foreground" />
              <span className="capitalize">{opportunity.employment_type.replace("-", " ")}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <DollarSign className="h-4 w-4 text-muted-foreground" />
              <span>{formatSalary(opportunity.salary_min, opportunity.salary_max)}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <Badge variant={disclosure.variant as any}>{disclosure.label}</Badge>
            </div>
          </div>

          {/* Description */}
          <div>
            <h3 className="font-semibold mb-2">Job Description</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">{opportunity.description}</p>
          </div>

          {/* Requirements */}
          {opportunity.requirements && (
            <div>
              <h3 className="font-semibold mb-2">Requirements</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{opportunity.requirements}</p>
            </div>
          )}

          {/* Posted Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>Posted {new Date(opportunity.created_at).toLocaleDateString()}</span>
          </div>

          {/* Application Section */}
          {!hasApplied ? (
            <div className="border-t pt-6 space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Cover Letter (Optional)
                </label>
                <Textarea
                  placeholder="Tell the employer why you're a great fit for this role..."
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
              </div>
              <Button onClick={handleApply} disabled={applying} className="w-full" size="lg">
                <Send className="h-4 w-4 mr-2" />
                {applying ? "Submitting..." : "Submit Application"}
              </Button>
            </div>
          ) : (
            <div className="border-t pt-6">
              <div className="bg-muted p-4 rounded-lg text-center">
                <p className="font-medium text-green-600">Application Submitted</p>
                <p className="text-sm text-muted-foreground mt-1">
                  You have already applied to this position
                </p>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
